import {
  Text,
  Modal,
  Button,
  Heading,
  useToast,
  ToastItem,
  DesignLanguage,
  useIsDesignLanguage,
  useModal,
  useDesignLanguagePerClass,
} from '@afterpaytouch/core'
import React, { FunctionComponent, useEffect, useState } from 'react'
import styles from './style.module.css'
import { useCheckoutSizes } from '../../utils/responsive'

import { useTranslation } from '../../utils/convergence'
import {
  dismissBiometricRegistrationModal,
  getFinishBiometricRegistrationCredentialIds,
  getStartRegistrationResponseData,
  setAllowDisplayingModal,
  setLocalBiometricConsumerCredentials,
  useAppDispatch,
  useConsumerSelector,
  useIsApplePaylater,
} from '../../state'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { getCredentialIds } from '@afterpay/utils/biometric'
import { BIOMETRIC_MAX_DENIED_COUNT, StartRegistrationResponseData } from '../../model'
import { create } from '@github/webauthn-json/browser-ponyfill'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { BiometricAnimation } from './BiometricAnimation'
import { BiometricRegistrationModalKey } from '../../model/modalKey'
import { useIsBiometricConflictingModal, useIsEligibleBiometricRegistration } from '../../state/biometric/hooks'

export const BiometricRegistrationModal: FunctionComponent = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const setToast = useToast()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { textSize, extraSmallTextSize, headingSize } = useCheckoutSizes()
  const { email, mobile } = useConsumerSelector()
  const [startRegistrationResponse, setStartRegistrationResponse] = useState<StartRegistrationResponseData | null>(null)
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const { visible, showModal, closeModal } = useModal(BiometricRegistrationModalKey)
  const designLanguageClasses = useDesignLanguagePerClass(styles)
  const isConflictingModalsWithBiometric = useIsBiometricConflictingModal()
  const isEligibleBiometricRegistration = useIsEligibleBiometricRegistration()
  const isApplePaylater = useIsApplePaylater()

  const onModalClick = (event: string): void => {
    logEvent(event)
    closeModal()
    dispatch(setAllowDisplayingModal(false))
  }

  const onCloseModal = (): void => {
    onModalClick(TrackingEvent.PRESSED_BIOMETRIC_OPT_IN_MODAL_CLOSE)
  }

  const biometricSuccessMessage = (): JSX.Element => (
    <>
      <ToastItem.Headline>{t('summary:biometricRegistrationModal.toast.heading')}</ToastItem.Headline>
      <ToastItem.Body>{t('summary:biometricRegistrationModal.toast.message')}</ToastItem.Body>
    </>
  )

  const onClickTurnOn = async (): Promise<void> => {
    if (startRegistrationResponse != null) {
      const { creationOptions, startBiometricRegistrationData } = startRegistrationResponse
      try {
        let credentialIds
        try {
          const credential = (await create(creationOptions)) as PublicKeyCredential
          credentialIds = await dispatch(getFinishBiometricRegistrationCredentialIds(startBiometricRegistrationData.requestId, credential))
        } catch (e) {
          if (e?.name === 'InvalidStateError') {
            const existingCredentials = startBiometricRegistrationData?.publicKeyCredentialCreationOptions?.excludeCredentials

            credentialIds = existingCredentials?.map((credential) => credential.id) ?? []
          } else {
            throw e
          }
        }
        handleRegistrationSuccess(credentialIds)
      } catch (e) {
        capture.error(e)
        logEvent(TrackingEvent.ENABLED_BIOMETRIC_LOGIN_FAILED, { error: e?.name, errorDetails: e?.message })
      }
    }
    onModalClick(TrackingEvent.PRESSED_BIOMETRIC_OPT_IN_MODAL_TURN_ON)
  }

  const handleRegistrationSuccess = (credentialIds: string[]): void => {
    logEvent(TrackingEvent.ENABLED_BIOMETRIC_LOGIN_SUCCESSFUL)

    const [storedCredentialIdsByEmail, storedCredentialIdsByMobile] = getCredentialIds(email, mobile)

    setLocalBiometricConsumerCredentials(email, mobile, storedCredentialIdsByEmail, storedCredentialIdsByMobile, credentialIds)

    setToast({ message: biometricSuccessMessage, kind: 'Success' })
  }

  const onClickMaybeLater = async (): Promise<void> => {
    try {
      const deniedCount = await dispatch(dismissBiometricRegistrationModal())
      if (deniedCount === BIOMETRIC_MAX_DENIED_COUNT) {
        logEvent(TrackingEvent.DECLINED_ALL_BIOMETRIC_OPT_INS)
      }
    } catch (e) {
      // Intentionally ignore errors from dismissBiometricRegistrationModal
    }

    onModalClick(TrackingEvent.PRESSED_BIOMETRIC_OPT_IN_MODAL_MAYBE_LATER)
  }

  useEffect(() => {
    const startBiometricRegistration = async (): Promise<void> => {
      try {
        const registerData = await dispatch(getStartRegistrationResponseData())
        setStartRegistrationResponse({
          startBiometricRegistrationData: registerData.startBiometricRegistrationData,
          creationOptions: registerData.creationOptions,
        })
      } catch (e) {
        capture.error(e)
        logEvent(TrackingEvent.ENABLED_BIOMETRIC_LOGIN_FAILED, { error: e?.name, errorDetails: e?.message })
      }
    }

    if (visible) {
      startBiometricRegistration()
    }
  }, [visible])

  useEffect(() => {
    if (isConflictingModalsWithBiometric) {
      closeModal()
    }
  }, [isConflictingModalsWithBiometric])

  useEffect(() => {
    if (isApplePaylater) {
      return
    }

    if (isEligibleBiometricRegistration) {
      showModal()
    }
  }, [isEligibleBiometricRegistration])

  return (
    <Modal
      id={BiometricRegistrationModalKey}
      showCloseButton={isBronteDesignLanguage}
      show={visible}
      onClose={onCloseModal}
      onHide={onCloseModal}
      onBackdropClick={onCloseModal}
    >
      <Modal.Content>
        <div className={designLanguageClasses.animationContainer}>
          <BiometricAnimation />
        </div>
        <Heading size={headingSize}>{t('summary:biometricRegistrationModal.headingNew')}</Heading>
        <div className={designLanguageClasses.contentAlign}>
          <Text size={textSize} color={isArcadeDesignLanguage ? 'Subtle' : undefined}>
            {t('summary:biometricRegistrationModal.message')}
          </Text>
          <Text size={extraSmallTextSize} color={isArcadeDesignLanguage ? 'Subtle' : 'Gray40'}>
            {t('summary:biometricRegistrationModal.disclaimer')}
          </Text>
        </div>
      </Modal.Content>

      <Modal.Footer>
        <Button
          disabled={startRegistrationResponse === null}
          testNameSpace='biometric-registration-turn-on'
          data-dd-action-name='Turn On Biometric Login'
          size='Large'
          kind='Primary'
          padding='Fluid'
          onClick={onClickTurnOn}
        >
          {t('summary:biometricRegistrationModal.turnOnLabel')}
        </Button>
        <div className={designLanguageClasses.buttonAlign}>
          <Button
            testNameSpace='biometric-registration-maybe-later'
            data-dd-action-name='Maybe Later Biometric Login'
            size='Regular'
            kind={isArcadeDesignLanguage ? 'Secondary' : 'Link'}
            padding='Fluid'
            onClick={onClickMaybeLater}
          >
            {t('summary:biometricRegistrationModal.maybeLaterLabel')}
          </Button>
        </div>
        <TrackOnMount eventName={TrackingEvent.VIEWED_BIOMETRIC_OPT_IN_MODAL} />
      </Modal.Footer>
    </Modal>
  )
}
